import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { useIsAuthenticated } from '@azure/msal-react';
import { footerLinks, socialLinks, unauthenticatedItems } from '@/constants/navigation-items';
import { Cookie } from 'lucide-react';
import CookieConsent from './CookieConsent';
import { useState } from 'react';
import { useUser } from '@/state/UserContext';
import { cookieService } from '@/services/CookieService';

function Footer() {
  const isAuthenticated = useIsAuthenticated();
  const [consentOpen, setConsentOpen] = useState<boolean>(false);
  const { geoLocation, isUserLoading } = useUser();
  const isGdprCountry = !isUserLoading && geoLocation?.country && cookieService.isGdprCountry(geoLocation.country);

  function closeCookieConsent() {
    setConsentOpen(false);
  }

  function openCookieConsent() {
    setConsentOpen(true);
  }

  return (
    <footer className='w-full bg-[#fcfcfc] border-t border-gray-200 text-black text-sm pt-10 py-6 print:hidden'>
      <div className='px-4 md:container mx-auto'>
        <div className='flex flex-col md:gap-8 md:flex-row justify-between'>
          <div className='order-2 md:order-1'>
            <div className='px-8 pb-5 border-t border-t-gray-200 md:hidden' aria-hidden />

            <img src='/logo-long.png' alt='Eklpix Research Logo' className='max-h-9 md:h-10' />
            <p className='mt-4 max-w-[300px] leading-tight text-gray-700'>
              eklipX Limited is a development company that focuses on creating innovative solutions for the future of
              commodity markets.
            </p>
          </div>

          <nav className='flex flex-wrap md:justify-end gap-6 order-1 mb-6'>
            {isAuthenticated ? (
              footerLinks.map((link) => {
                if (link.children) {
                  return (
                    <div key={link.label}>
                      <p className='font-bold text-gray-800 mb-2 tracking-3'>{link.label}</p>
                      <ul className='flex flex-col gap-2'>
                        {link.children.map((childLink) => (
                          <li key={childLink.label} className='mr-4'>
                            <Link to={childLink.path} className=' text-gray-600 hover:text-gray-500'>
                              {childLink.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                }
              })
            ) : (
              <div>
                <p className='font-bold text-gray-800 mb-2 tracking-3'>Links</p>
                <ul className='flex flex-col gap-2'>
                  {unauthenticatedItems.map((link) => (
                    <li key={link.label} className='mr-4'>
                      {link.hash ? (
                        <HashLink smooth to={`${link.path}${link.hash}`} className=' text-gray-600 hover:text-gray-500'>
                          {link.label}
                        </HashLink>
                      ) : (
                        <Link to={link.path} className=' text-gray-600 hover:text-gray-500'>
                          {link.label}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div>
              <p className='font-bold text-gray-800 mb-2 tracking-3'>Accounts</p>
              <ul className='flex flex-col gap-2'>
                {socialLinks.map((link) => (
                  <li key={link.label} className='mr-4'>
                    <a
                      href={link.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className=' text-gray-600 hover:text-gray-500'
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </nav>
        </div>

        <div className='mt-5 pt-2 flex items-center justify-center text-center text-sm text-gray-500'>
          <p>© {new Date().getFullYear()} eklipX Limited</p>
          {isGdprCountry && (
            <button
              data-cy='cookie-settings'
              onClick={openCookieConsent}
              className='border-l pl-2 ml-2 py-0 border-l-gray-500 hover:underline'
            >
              Cookie Settings <Cookie className='size-3 inline mb-[1px]' />
            </button>
          )}
        </div>
      </div>

      <CookieConsent consentOpen={consentOpen} showCloseButton={consentOpen} onClose={closeCookieConsent} />
    </footer>
  );
}

export default Footer;
